import dayjs from 'dayjs';

import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {GetTasksWithDateRangeForExportQuery} from '@/services/graphqlHooks';
import {useGetTasksWithDateRangeForExportQuery} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import DateRangeOptionsFields from '../../components/export-options/DateRangeOptionsFields';
import type {ExportDefinition} from '../../types/exportTypes';
import type {DateRangeOptions} from '../exportOptions';
import {
  dateRangeOptionsSchema,
  describeDateRange,
  formatExportDate,
  formatExportDateTime,
  formatPersonName,
  getDateRangeDefaultOptions,
  getDateRangeFileName,
} from '../exportOptions';

type TaskExportRecord =
  GetTasksWithDateRangeForExportQuery['getTasksWithDateRange'][number] & {
    residentName: string;
    employeeName: string;
    completedByName: string;
  };

export const tasksExport: ExportDefinition<DateRangeOptions, TaskExportRecord> =
  {
    id: 'tasks',
    label: i18n.t('Tasks'),
    permission: 'EXPORT_TASKS',
    entitlement: 'TASKS',
    optionsSchema: dateRangeOptionsSchema,
    OptionsFields: DateRangeOptionsFields,
    getDefaultOptions: getDateRangeDefaultOptions,
    load: async options => {
      const variables = {input: options};
      const {getTasksWithDateRange, getAllCurrentResidents, getAllUsers} =
        await queryClient.fetchQuery({
          queryKey: useGetTasksWithDateRangeForExportQuery.getKey(variables),
          queryFn: useGetTasksWithDateRangeForExportQuery.fetcher(
            graphqlClient,
            variables
          ),
        });
      const residents = new Map(
        getAllCurrentResidents.map(resident => [resident.id, resident.name])
      );
      const usersById = new Map(getAllUsers.map(user => [user.id, user.name]));
      const usersByUserName = new Map(
        getAllUsers.map(user => [user.userName, user.name])
      );

      return getTasksWithDateRange
        .map(task => ({
          ...task,
          residentName: formatPersonName(
            task.assignedToResident
              ? residents.get(task.assignedToResident)
              : undefined
          ),
          employeeName: formatPersonName(
            task.assignedToEmployee
              ? usersById.get(task.assignedToEmployee)
              : undefined
          ),
          completedByName: task.completedBy
            ? formatPersonName(usersByUserName.get(task.completedBy)) ||
              task.completedBy
            : '',
        }))
        .sort((taskA, taskB) => dayjs(taskA.date).diff(taskB.date));
    },
    columns: [
      {header: i18n.t('Date'), value: task => formatExportDate(task.date)},
      {header: i18n.t('Title'), value: task => task.title},
      {header: i18n.t('Content'), value: task => task.content},
      {
        header: i18n.t('Resident'),
        filterable: true,
        value: task => task.residentName,
      },
      {
        header: i18n.t('Employee'),
        filterable: true,
        value: task => task.employeeName,
      },
      {
        header: i18n.t('Completed at'),
        value: task => formatExportDateTime(task.completedAt),
      },
      {
        header: i18n.t('Completed by'),
        filterable: true,
        value: task => task.completedByName,
      },
    ],
    describeOptions: describeDateRange,
    getFileName: options => getDateRangeFileName('tasks', options),
  };
